import {
  Component,
  HostListener,
  Inject,
  Input,
  OnInit
} from '@angular/core';
import {ActivatedRoute, Data, Router} from '@angular/router';
import {TranslocoService} from '@ngneat/transloco';
import {environment} from "@environment";
import {AbstractComponent, CurrentItemType} from "@components";
import {
  CategoryItem,
  Competitor,
  CompetitorResults,
  EventItem,
  Lookups,
  Race,
  RaceCommercialItem,
  RaceRoute, ScoringForType,
  Waypoint,
  WaypointType
} from "@model";
import {RaceRouteService, RaceService} from "@services";
import {
  REGISTER_TO_RACE
} from "../../../app-routing.module";
import {getLanguageValue} from "@utility";
import {DOCUMENT} from "@angular/common";

@Component({
    selector: 'app-race-details',
    templateUrl: './race-details.component.html',
    styleUrls: ['./race-details.component.scss'],
    standalone: false
})
export class RaceDetailsComponent extends AbstractComponent implements OnInit {
  isPreviewComponent: boolean = false;

  @Input('eventDetails') eventItem: EventItem | undefined;
  @Input('raceDetails') race: Race | undefined;
  @Input() raceCommercialItems: RaceCommercialItem[] | undefined;
  @Input() competitors: Competitor[] = [];
  @Input() results: CompetitorResults[] = [];
  @Input({required: true}) languages: Lookups[] = [];
  @Input() waypointList: Waypoint[] = [];
  @Input() countries: Lookups[] = [];
  @Input() categories: CategoryItem[] = [];
  @Input() raceRoute: RaceRoute | undefined;
  @Input() genders: Lookups[] = [];

  lat: number = environment.headquartersLat;
  lng: number = environment.headquartersLng;
  currentWindowWidth: number = 0;
  currentUrl: string = "";
  startWaypoint?: Waypoint;
  finishWaypoint?: Waypoint;
  controlPointsList: Waypoint[] = [];

  constructor(@Inject(DOCUMENT) private document: Document,
              private raceService: RaceService, private route: ActivatedRoute, public translocoService: TranslocoService,
              private router: Router, private raceRouteService: RaceRouteService) {
    super();
    if (this.isBrowser) {
      this.currentWindowWidth = window.innerWidth;
    }
  }

  ngOnInit(): void {
    this.currentUrl = this.document.location.href;
    this.subscribeToRouteDataChange();
    this.findStartAndEndWaypoint();
    this.filterControlPoints();
    if (this.isBrowser) {
      this.downloadGpx();
    }
  }

  private subscribeToRouteDataChange(): void {
    this.subscriptions.push(
      this.route.data.subscribe(({isPreview}: Data) => {
        this.isPreviewComponent = isPreview ?? false;
      })
    )
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.currentWindowWidth = event.target.innerWidth;
  }

  onRegister(): void {
    if (this.race) {
      if (this.race.raceSettings?.registrationExternalUrl) {
        window.open(this.race.raceSettings.registrationExternalUrl);
      } else {
        this.router.navigate([REGISTER_TO_RACE], {
          relativeTo: this.route,
          queryParams: {raceId: this.race.id, eventId: this.race.eventId},
          queryParamsHandling: "merge"
        });
      }
    }
  }

  findStartAndEndWaypoint(): void {
    if (this.waypointList && this.waypointList.length > 0) {
      for (let waypoint of this.waypointList) {
        if (waypoint.waypointTypeId === WaypointType.START_WAYPOINT) {
          this.startWaypoint = waypoint;
          if (this.startWaypoint.latitude) {
            this.lat = this.startWaypoint.latitude;
          }
          if (this.startWaypoint.longitude) {
            this.lng = this.startWaypoint.longitude;
          }
        } else if (waypoint.waypointTypeId === WaypointType.FINISH_WAYPOINT) {
          this.finishWaypoint = waypoint;
        }
      }
    }
  }

  filterControlPoints(): void {
    if ((this.waypointList.length ?? 0) > 0) {
      this.controlPointsList = this.waypointList.filter((item: Waypoint): boolean => item.waypointTypeId === WaypointType.CONTROL_WAYPOINT);
    }
  }

  downloadGPXFile(): void {
    if (this.raceRoute?.content && this.race?.raceSettings?.enableGPXDownload) {
      const filename: string = `${(getLanguageValue(this.race?.name, this.race?.nameEng, this.race?.nameIta, this.race?.nameSlo, this.race?.nameSr,  this.race?.nameBs, this.translocoService.getActiveLang()) ?? "").split(/\s+/).join("_")}_route.gpx`;
      const decodedData: string = atob(this.raceRoute.content.split(",")[1]);

      const blob: Blob = new Blob([decodedData], {type: 'application/gpx+xml'});
      const link: HTMLAnchorElement = this.document.createElement('a');

      link.href = window.URL.createObjectURL(blob);
      link.download = filename;

      this.document.body.appendChild(link);
      link.click();
      this.document.body.removeChild(link);
    }
  }

  private downloadGpx() {
    if (this.raceRoute && this.raceRoute.route && this.raceRoute.route !== "") {
      this.raceRouteService.downloadGpx(this.raceRoute.route, (base64data: string) => {
        this.raceRoute!.content = base64data;
      });
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.raceService.emptyRaces();
  }

  protected readonly ScoringForType = ScoringForType;
  protected readonly CurrentItemType = CurrentItemType;
}
